import { useEffect, useRef, useState } from 'react';
import { adminApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { MapPin, RefreshCcw, Clock, User, Wifi, WifiOff, Square } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useConfirm } from './ConfirmProvider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const POLL_MS = 20_000;

function formatAge(seconds) {
  if (seconds == null) return 'no ping';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

function pingTone(seconds) {
  if (seconds == null) return 'text-slate-500';
  if (seconds <= 90) return 'text-emerald-400';
  if (seconds <= 5 * 60) return 'text-amber-400';
  return 'text-red-400';
}

export function LiveShiftsPanel() {
  const [shifts, setShifts] = useState([]);
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const [forcingId, setForcingId] = useState(null);
  const timerRef = useRef(null);

  const forceClockOut = async (shift) => {
    const hrs = shift.hours_so_far ?? '?';
    const reason = await confirm({ title: `Force-clock ${shift.worker_name} off?`, description: `They have been clocked in for ${hrs}h. This ends their shift now and notifies them.`, withInput: true, inputLabel: 'Optional reason (visible to the worker)', confirmText: 'Clock off', destructive: true });
    if (reason === null) return; // cancelled
    setForcingId(shift.id);
    try {
      await axios.post(`${API}/clock/${shift.id}/force-out`, { reason: reason || null }, { headers: authHeader() });
      toast.success(`${shift.worker_name} has been clocked off.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clock off');
    } finally {
      setForcingId(null);
    }
  };

  const load = async () => {
    try {
      const { data } = await adminApi.getActiveShifts();
      setShifts(data.active_shifts || []);
      setLastFetched(new Date());
    } catch (_) {
      // silent — keep last data on the screen
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  const totalKm = shifts.reduce((s, x) => s + (x.total_km || 0), 0);

  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="live-shifts-panel">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-white flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${shifts.length ? 'bg-emerald-400 animate-ping' : 'bg-slate-500'}`}></span>
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${shifts.length ? 'bg-emerald-500' : 'bg-slate-500'}`}></span>
            </span>
            Live Shifts
            <Badge variant="outline" className="border-slate-600 text-slate-300 ml-1" data-testid="live-shifts-count">
              {shifts.length} active
            </Badge>
            <Badge variant="outline" className="border-teal-600/50 text-teal-300" data-testid="live-shifts-total-km">
              {totalKm.toFixed(1)} km combined
            </Badge>
          </CardTitle>
          <p className="text-xs text-slate-400 mt-1">
            {lastFetched ? `Updated ${lastFetched.toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit', second: '2-digit' })} · auto-refresh every 20s` : 'Loading...'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="border-slate-700 text-slate-200 hover:bg-slate-700"
          data-testid="live-shifts-refresh"
        >
          <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </CardHeader>

      <CardContent className="pt-0">
        {shifts.length === 0 ? (
          <div className="py-10 text-center text-slate-500" data-testid="live-shifts-empty">
            <Clock className="mx-auto h-8 w-8 mb-2 text-slate-600" />
            No workers currently clocked in.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Worker</TableHead>
                  <TableHead className="text-slate-400">Client</TableHead>
                  <TableHead className="text-slate-400">Clock-in</TableHead>
                  <TableHead className="text-slate-400 text-right">Hours</TableHead>
                  <TableHead className="text-slate-400 text-right">Live KMs</TableHead>
                  <TableHead className="text-slate-400">Last ping</TableHead>
                  <TableHead className="text-slate-400 text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shifts.map((s) => (
                  <TableRow key={s.id} className="border-slate-700" data-testid={`live-shift-row-${s.worker_id}`}>
                    <TableCell className="text-white font-medium">
                      <span className="inline-flex items-center gap-2">
                        <User className="h-3.5 w-3.5 text-slate-400" />
                        {s.worker_name}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {s.client_name || <span className="text-slate-500 italic">unassigned</span>}
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm">
                      {s.clock_in
                        ? new Date(s.clock_in).toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right text-slate-200 tabular-nums">
                      {s.hours_so_far != null ? `${s.hours_so_far}h` : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="border-teal-500/40 text-teal-300 tabular-nums" data-testid={`live-shift-km-${s.worker_id}`}>
                        <MapPin className="h-3 w-3 mr-1" />
                        {s.total_km.toFixed(2)} km
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-xs ${pingTone(s.last_ping_age_seconds)}`}>
                        {s.last_ping_age_seconds != null && s.last_ping_age_seconds <= 5 * 60
                          ? <Wifi className="h-3 w-3" />
                          : <WifiOff className="h-3 w-3" />}
                        {formatAge(s.last_ping_age_seconds)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={forcingId === s.id}
                        onClick={() => forceClockOut(s)}
                        className="h-7 px-2 border-red-500/50 text-red-300 hover:bg-red-500/10"
                        data-testid={`force-clock-off-${s.worker_id}`}
                      >
                        <Square className="h-3 w-3 mr-1 fill-current" />
                        {forcingId === s.id ? '...' : 'Clock off'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
